import { useRef, useCallback, useEffect } from 'react';

const useDebouncedSessionStorage = (key, delay = 500) => {
  const timeoutRef = useRef(null);
  const pendingDataRef = useRef(null);

  const debouncedSetItem = useCallback((data) => {
    pendingDataRef.current = data;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      try {
        if (pendingDataRef.current !== null) {
          sessionStorage.setItem(key, JSON.stringify(pendingDataRef.current));
          pendingDataRef.current = null;
        }
      } catch (error) {
        console.error("Error saving to sessionStorage:", error);
        if (error.name === 'QuotaExceededError') {
          console.warn("SessionStorage quota exceeded. Clearing old data...");
        }
      }
    }, delay);
  }, [key, delay]);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingDataRef.current !== null) {
      try {
        sessionStorage.setItem(key, JSON.stringify(pendingDataRef.current));
        pendingDataRef.current = null;
      } catch (error) {
        console.error("Error flushing sessionStorage:", error);
      }
    }
  }, [key]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { debouncedSetItem, flush };
};

export default useDebouncedSessionStorage;

